export const PENDING  = 0;
export const APPROVED = 1;
export const REJECTED = 2;

const hasValue = (v) =>
  v !== undefined && v !== null && String(v).trim() !== '' && String(v) !== 'null';

/**
 * Normalised flag: 0 (pending) | 1 (approved) | 2 (rejected).
 *
 * HED_IS_APPROVED is the ONLY source of truth. GetEmployees sends it as a
 * string — "True" / "False" — and note that "False" is truthy in JavaScript, so
 * it has to be compared, never just tested.
 *
 *   "True"  / true / 1        → Approved   (the Employees tab)
 *   "2"                       → Rejected
 *   "False" / false / 0 /
 *   null / missing            → Pending    (the Approvals tab)
 *
 * There is deliberately no fallback to HED_APPROVED_BY / HED_APPROVED_DATE: a
 * row carrying an approver but no flag is NOT approved, and must not slip into
 * the Employees tab.
 */
export function approvalState(emp) {
  if (!emp) return PENDING;

  const flag = emp.HED_IS_APPROVED ?? emp.p_isApproved ?? emp.IS_APPROVED;
  if (!hasValue(flag)) return PENDING;

  if (flag === true)  return APPROVED;
  if (flag === false) return PENDING;

  const s = String(flag).trim().toLowerCase();
  if (s === 'true'  || s === '1' || s === 'yes') return APPROVED;
  if (s === 'false' || s === '0' || s === 'no')  return PENDING;
  if (s === '2' || s === 'rejected')             return REJECTED;
  return PENDING;
}

export const isApproved        = (emp) => approvalState(emp) === APPROVED;
export const isPendingApproval = (emp) => approvalState(emp) === PENDING;
export const isRejected        = (emp) => approvalState(emp) === REJECTED;

export function countPendingApproval(list = []) {
  return list.reduce((n, e) => n + (isPendingApproval(e) ? 1 : 0), 0);
}

const pad2 = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD HH:mm:ss' in local time — what the API expects. */
export function approvalTimestamp(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
         `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** Readable form of a stored approval date, for display only. */
export function formatApprovedDate(v) {
  if (!hasValue(v)) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ` +
         `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Shared shape for both decisions — only p_isApproved differs. */
function decisionPayload(emp, decidedBy, isApprovedValue) {
  return {
    p_hedServiceNo: emp?.HED_SERVICE_NO || emp?.p_ced_service_no || '',
    p_isApproved: isApprovedValue,
    p_hedApprovedBy: decidedBy,
    p_hedApprovedDate: approvalTimestamp(),
  };
}

/** Approve → p_isApproved: 1 */
export function buildApprovalPayload(emp, approvedBy) {
  return decisionPayload(emp, approvedBy, APPROVED);
}

/** Reject → p_isApproved: 2 */
export function buildRejectionPayload(emp, rejectedBy) {
  return decisionPayload(emp, rejectedBy, REJECTED);
}

/** Value sent for a brand-new employee — always unapproved. */
export const NEW_EMPLOYEE_IS_APPROVED = PENDING;